import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Save, Check } from "lucide-react";

interface HeadlineDefaultTagManagerProps {
  allTags: string[];
}

export const HeadlineDefaultTagManager = ({ allTags }: HeadlineDefaultTagManagerProps) => {
  const [currentDefaultTag, setCurrentDefaultTag] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchDefaultTag();
  }, []);

  const fetchDefaultTag = async () => {
    try {
      const { data, error } = await supabase
        .from('headline_settings')
        .select('setting_value')
        .eq('setting_key', 'default_organic_tag')
        .maybeSingle();

      if (error) throw error;

      const tag = data?.setting_value || null;
      setCurrentDefaultTag(tag);
      setSelectedTag(tag);
    } catch (error) {
      console.error('Error fetching default tag:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się pobrać domyślnego tagu",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('headline_settings')
        .update({ 
          setting_value: selectedTag,
          updated_at: new Date().toISOString()
        })
        .eq('setting_key', 'default_organic_tag');

      if (error) throw error;

      setCurrentDefaultTag(selectedTag);
      
      toast({
        title: "Sukces",
        description: selectedTag 
          ? `Tag "${selectedTag}" został ustawiony jako domyślny dla ruchu organicznego`
          : "Domyślny tag został usunięty - będzie losowany z wszystkich aktywnych wariantów",
      });
    } catch (error) {
      console.error('Error saving default tag:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się zapisać ustawień",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = currentDefaultTag !== selectedTag;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-sm text-muted-foreground">Ładowanie...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Domyślny tag dla ruchu organicznego
          {currentDefaultTag && (
            <Badge variant="secondary" className="ml-2">
              <Check className="h-3 w-3 mr-1" />
              {currentDefaultTag}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Wybierz tag, który będzie automatycznie przypisywany użytkownikom wchodzącym bez parametru ?tag w URL.
          {!currentDefaultTag && " Obecnie losowany jest spośród wszystkich aktywnych wariantów."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block">
              Wybierz domyślny tag
            </label>
            <Select value={selectedTag || "none"} onValueChange={(value) => setSelectedTag(value === "none" ? null : value)}>
              <SelectTrigger>
                <SelectValue placeholder="Wybierz tag..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">Brak (losuj z wszystkich)</span>
                </SelectItem>
                {allTags.map(tag => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="min-w-[100px]"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? "Zapisywanie..." : "Zapisz"}
          </Button>
        </div>

        {selectedTag && (
          <div className="text-sm space-y-2 p-4 bg-muted/50 rounded-lg">
            <div className="font-medium">Efekt po zapisaniu:</div>
            <ul className="space-y-1 text-muted-foreground">
              <li>✓ Użytkownicy wchodzący na <code className="text-xs bg-background px-1 py-0.5 rounded">/</code> dostaną warianty z tagiem <strong>{selectedTag}</strong></li>
              <li>✓ Użytkownicy z <code className="text-xs bg-background px-1 py-0.5 rounded">/?tag={selectedTag}</code> dostaną te same warianty</li>
              <li>✓ Inne tagi (np. <code className="text-xs bg-background px-1 py-0.5 rounded">/?tag=B2B</code>) będą działać normalnie</li>
              <li>✓ W statystykach zobaczysz rozróżnienie między ruchem organicznym a kampaniami</li>
            </ul>
          </div>
        )}

        {!selectedTag && hasChanges && (
          <div className="text-sm space-y-2 p-4 bg-muted/50 rounded-lg">
            <div className="font-medium">Efekt po zapisaniu:</div>
            <ul className="space-y-1 text-muted-foreground">
              <li>✓ Użytkownicy wchodzący na <code className="text-xs bg-background px-1 py-0.5 rounded">/</code> dostaną losowy wariant ze wszystkich aktywnych</li>
              <li>✓ Kampanie z parametrem ?tag nadal będą działać normalnie</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
